import React, { memo } from "react";
import { Handle, Position } from "react-flow-renderer";
import { Typography, Chip } from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import { ContentCopy, Delete, ArrowForwardIos, Autorenew, CheckCircle } from "@mui/icons-material";
import { useNodeStorage } from "../../../stores/useNodeStorage";

const operatorLabels = {
  equals_to: "=",
  not_equals_to: "≠",
  greater_than: ">",
  less_than: "<",
  greater_or_equal: "≥",
  less_or_equal: "≤",
  contains: "contém",
  not_contains: "não contém",
  starts_with: "começa com",
  ends_with: "termina com",
  is_empty: "está vazio",
  is_not_empty: "não está vazio",
  isTrue: "é verdadeiro",
  isFalse: "é falso",
};

function WhileNode({ data, isConnectable, id }) {
  const theme = useTheme();
  const storageItems = useNodeStorage();

  const variable = data?.variable || "${variavel}";
  const operator = data?.operator || "equals_to";
  const value = data?.value || "";
  const maxIterations = data?.maxIterations || 100;

  const truncateText = (text, maxLength = 15) => {
    if (!text) return "";
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  const needsRightValue = !["is_empty", "is_not_empty", "isTrue", "isFalse"].includes(operator);

  return (
    <div
      style={{
        backgroundColor: theme.palette.background.paper,
        padding: theme.spacing(2),
        borderRadius: theme.shape.borderRadius,
        border: `2px solid #FF6F00`,
        boxShadow: `0 4px 8px rgba(0, 0, 0, 0.15)`,
        position: "relative",
        transition: "all 0.2s ease",
        width: "auto",
        minWidth: "220px",
        maxWidth: "280px",
        boxSizing: "border-box",
      }}
    >
      {/* Handle de entrada */}
      <Handle
        type="target"
        position={Position.Left}
        style={{
          background: "transparent",
          width: "28px",
          height: "28px",
          left: "-14px",
          top: "50%",
          transform: "translateY(-50%)",
          cursor: "crosshair",
          border: "none",
        }}
        isConnectable={isConnectable}
      >
        <div
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
          }}
        >
          <div
            style={{
              background: `linear-gradient(145deg, #FF6F00 0%, #E65100 100%)`,
              width: "20px",
              height: "20px",
              borderRadius: "50%",
              boxShadow: theme.shadows[2],
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: "none",
            }}
          >
            <ArrowForwardIos
              style={{
                color: "#fff",
                width: "12px",
                height: "12px",
                transform: "rotate(180deg)",
              }}
            />
          </div>
        </div>
      </Handle>

      {/* Botoes de acao */}
      <div
        style={{
          display: "flex",
          position: "absolute",
          right: theme.spacing(1),
          top: theme.spacing(1),
          gap: theme.spacing(1),
          cursor: "pointer",
        }}
      >
        <ContentCopy
          onClick={() => {
            storageItems.setNodesStorage(id);
            storageItems.setAct("duplicate");
          }}
          style={{
            width: "16px",
            height: "16px",
            color: theme.palette.primary.main,
          }}
        />
        <Delete
          onClick={() => {
            storageItems.setNodesStorage(id);
            storageItems.setAct("delete");
          }}
          style={{
            width: "16px",
            height: "16px",
            color: theme.palette.error.main,
          }}
        />
      </div>

      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: theme.spacing(1),
          marginBottom: theme.spacing(1.5),
        }}
      >
        <Autorenew
          style={{
            width: "18px",
            height: "18px",
            color: "#FF6F00",
          }}
        />
        <div
          style={{
            color: theme.palette.text.primary,
            fontSize: "15px",
            fontWeight: 500,
          }}
        >
          While (Enquanto)
        </div>
      </div>

      {/* Condição */}
      <div
        style={{
          backgroundColor: theme.palette.background.default,
          borderRadius: theme.shape.borderRadius,
          padding: theme.spacing(1.5),
          border: `1px solid ${theme.palette.divider}`,
          marginBottom: theme.spacing(1),
        }}
      >
        <Typography
          variant="caption"
          style={{
            color: theme.palette.text.secondary,
            fontWeight: 600,
            display: "block",
            marginBottom: 4,
          }}
        >
          Enquanto:
        </Typography>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 4, alignItems: "center" }}>
          <Chip
            size="small"
            label={truncateText(variable)}
            title={variable}
            style={{
              backgroundColor: "rgba(255, 111, 0, 0.15)",
              color: "#FF6F00",
              fontWeight: 500,
              fontSize: "10px",
            }}
          />
          <Typography variant="caption" style={{ color: "#FF6F00", fontWeight: 600 }}>
            {operatorLabels[operator] || operator}
          </Typography>
          {needsRightValue && (
            <Chip
              size="small"
              label={truncateText(value) || "..."}
              title={value}
              style={{
                backgroundColor: "rgba(255, 111, 0, 0.15)",
                color: "#FF6F00",
                fontWeight: 500,
                fontSize: "10px",
              }}
            />
          )}
        </div>
      </div>

      {/* Max Iterations */}
      <div
        style={{
          backgroundColor: "rgba(255, 111, 0, 0.08)",
          borderRadius: 4,
          padding: "4px 8px",
          border: `1px solid rgba(255, 111, 0, 0.2)`,
          marginBottom: theme.spacing(1),
        }}
      >
        <Typography
          variant="caption"
          style={{ color: theme.palette.text.secondary, fontSize: "10px" }}
        >
          Máx: <strong style={{ color: "#FF6F00" }}>{maxIterations} iterações</strong>
        </Typography>
      </div>

      {/* Labels das saídas */}
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          marginTop: theme.spacing(1.5),
          gap: theme.spacing(2),
          paddingRight: theme.spacing(1),
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 4,
          }}
        >
          <Autorenew style={{ color: "#FF6F00", width: 14, height: 14 }} />
          <Typography variant="caption" style={{ color: "#FF6F00", fontWeight: 500 }}>
            Loop
          </Typography>
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 4,
          }}
        >
          <CheckCircle style={{ color: "#4caf50", width: 14, height: 14 }} />
          <Typography variant="caption" style={{ color: "#4caf50", fontWeight: 500 }}>
            Fim
          </Typography>
        </div>
      </div>

      {/* Handle de saída LOOP */}
      <Handle
        type="source"
        position={Position.Right}
        id="loop"
        style={{
          background: "transparent",
          width: "28px",
          height: "28px",
          right: "-14px",
          top: "35%",
          cursor: "crosshair",
          border: "none",
        }}
        isConnectable={isConnectable}
      >
        <div
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
          }}
        >
          <div
            style={{
              background: `linear-gradient(145deg, #FF6F00 0%, #E65100 100%)`,
              width: "20px",
              height: "20px",
              borderRadius: "50%",
              boxShadow: theme.shadows[2],
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: "none",
            }}
          >
            <Autorenew
              style={{
                color: "#fff",
                width: "12px",
                height: "12px",
              }}
            />
          </div>
        </div>
      </Handle>

      {/* Handle de saída DONE */}
      <Handle
        type="source"
        position={Position.Right}
        id="done"
        style={{
          background: "transparent",
          width: "28px",
          height: "28px",
          right: "-14px",
          top: "75%",
          cursor: "crosshair",
          border: "none",
        }}
        isConnectable={isConnectable}
      >
        <div
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
          }}
        >
          <div
            style={{
              background: `linear-gradient(145deg, #4caf50 0%, #388e3c 100%)`,
              width: "20px",
              height: "20px",
              borderRadius: "50%",
              boxShadow: theme.shadows[2],
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: "none",
            }}
          >
            <CheckCircle
              style={{
                color: "#fff",
                width: "12px",
                height: "12px",
              }}
            />
          </div>
        </div>
      </Handle>
    </div>
  );
}

export default memo(WhileNode);
